
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Camera, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

export interface LogoUploaderProps {
  logoUrl?: string;
  fallback?: string;
  fallbackBgColor?: string;
  fallbackTextColor?: string;
  handleLogoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isLoading?: boolean;
  variant?: 'avatar' | 'upload-box';
  size?: 'sm' | 'md' | 'lg';
  maxSizeInMB?: number;
  label?: string;
}

export function LogoUploader({
  logoUrl,
  fallback = "",
  fallbackBgColor = "bg-brand-teal",
  fallbackTextColor = "text-white",
  handleLogoChange,
  isLoading = false,
  variant = 'avatar',
  size = 'md',
  maxSizeInMB = 1,
  label = "Logo"
}: LogoUploaderProps) {
  const fileInputRef = useState<HTMLInputElement | null>(null)[1];
  const { toast } = useToast();
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file size
      if (file.size > maxSizeInMB * 1024 * 1024) {
        toast({
          title: "Arquivo muito grande",
          description: `A imagem deve ter no máximo ${maxSizeInMB}MB`,
          variant: "destructive",
        });
        return;
      }
      
      handleLogoChange(e);
    }
  };

  const sizeClasses = {
    sm: "h-16 w-16",
    md: "h-24 w-24",
    lg: "h-32 w-32",
  };

  if (variant === 'avatar') {
    return (
      <div className="relative">
        <Avatar className={sizeClasses[size]}>
          <AvatarImage src={logoUrl} />
          <AvatarFallback className={`text-2xl ${fallbackBgColor} ${fallbackTextColor}`}>
            {fallback}
          </AvatarFallback>
        </Avatar>
        <label htmlFor="logo-upload" className="absolute bottom-0 right-0 bg-primary text-primary-foreground p-1 rounded-full cursor-pointer">
          <Camera className="h-4 w-4" />
          <input 
            type="file" 
            id="logo-upload" 
            className="sr-only" 
            accept="image/*"
            onChange={handleFileChange}
            disabled={isLoading}
          />
        </label>
      </div>
    );
  } else {
    return (
      <div className="space-y-1">
        <div 
          className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-3 bg-gray-50 hover:bg-gray-100 transition cursor-pointer" 
          onClick={() => !isLoading && document.getElementById('logo-box-upload')?.click()}
        >
          {logoUrl ? (
            <div className="flex flex-col items-center gap-2">
              <img src={logoUrl} alt="Logo preview" className="h-20 w-auto object-contain mb-1" />
              <Button variant="outline" size="sm" type="button" disabled={isLoading}>
                Alterar {label}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center py-2">
              <Upload className="h-6 w-6 text-gray-400 mb-1" />
              <p className="text-sm text-gray-500">Clique para fazer upload</p>
              <p className="text-xs text-gray-400 mt-1">PNG, JPG (máx. {maxSizeInMB}MB)</p>
            </div>
          )}
          <input
            id="logo-box-upload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
            disabled={isLoading}
          />
        </div>
      </div>
    );
  }
}
